import * as React from 'react';

import requestCountry from 'js/lib/requestCountry';

import { IdTypes, createFeatureScope } from 'bundles/product-features';
import EnterpriseProductFeaturesContext from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

const UNITED_STATES_COUNTRY_CODE = 'US';

const { useProductFeatures } = createFeatureScope({
  enterprise: {
    enableCareerAcademyLaborMarketData: true,
    enableCareerAcademyTestimonialsComponent: true,
    enableSkillsInSearchAndBrowse: true,
    isCareerAcademyLimitedCatalog: false,
    browseOnlyProgram: false,
    programSponsoredByMessage: true,
    enableSkillsDashboard: false,
    programJoinTrackingVerbiage: true,
    allowOrgForCurriculumBuilder: false,
    programRecommendationsAvailableCoursesCopy: false,
    enableCareerSupport: false,
    isRolePageEstimatedCompletionTimeDisabled: false,
    personalizedRecommendations: true,
    programEulaBanner: false,
    allowOrgForSpecializationConfiguration: false,
    enableAcademicDisciplines: false,
    programCollectionSectionDescription: true,
    programHideCourseraRecommendations: false,
    programJoinAgeVerification: false,
    enableEnterpriseCustomBadges: false,
    allowShortFormContentBasedOnAudience: true,
    enableRenderDomainsMenu: true,
    enableInvitedOnlyProgram: false,
    enableSkillsetRoleFilter: true,
    stackabilityOnXdpForEnterpriseLearners: true,
    enableGenAiHubPages: false,
    enableOptimizedLearnerFlow: false,
    enableEnterpriseTermsOfUseModal: false,
    filledProgramCustomMessage: '',
    enableForceSsoLogin: false,
    enableForceSsoLoginV2Beta: false,
    enableOptimisedEnrollmentSetting: false,
    whitelistOrganizationProgramsForSSOLogin: false,
    enableUnifiedSso: false,
    enableGenericNotificationMessaging: false,
    enableProgramAutoJoin: false,
  },
});

const EnterpriseProductFeaturesDataProvider = ({ children }: { children: React.ReactNode }) => {
  const program = useProgram();
  const thirdPartyOrganization = useThirdPartyOrganization();

  const { features: orgFeatures, loading: isOrgProductFeaturesLoading } = useProductFeatures({
    idType: IdTypes.Organization,
    id: thirdPartyOrganization?.id || '',
    skip: !thirdPartyOrganization?.id,
    ssr: true,
  });

  const { features: programFeatures, loading: isProgramProductFeaturesLoading } = useProductFeatures({
    idType: IdTypes.Program,
    id: program?.id || '',
    skip: !program?.id,
    ssr: true,
  });

  if (!program?.id || !thirdPartyOrganization?.id) {
    return (
      <EnterpriseProductFeaturesContext.Provider value={undefined}>
        {children}
      </EnterpriseProductFeaturesContext.Provider>
    );
  } else {
    return (
      <EnterpriseProductFeaturesContext.Provider
        value={{
          features: {
            isCareerAcademyLimitedCatalogProgram: programFeatures.get('enterprise', 'isCareerAcademyLimitedCatalog'),
            enableSkillsDashboard:
              orgFeatures.get('enterprise', 'enableSkillsDashboard') ||
              programFeatures.get('enterprise', 'enableSkillsDashboard'),
            enableSkillsInSearchAndBrowse: programFeatures.get('enterprise', 'enableSkillsInSearchAndBrowse'),
            enableCareerAcademyLaborMarketData:
              programFeatures.get('enterprise', 'enableCareerAcademyLaborMarketData') &&
              requestCountry.get() === UNITED_STATES_COUNTRY_CODE,
            enableCareerAcademyTestimonialsComponent: programFeatures.get(
              'enterprise',
              'enableCareerAcademyTestimonialsComponent'
            ),
            isBrowseOnlyProgram:
              orgFeatures.get('enterprise', 'browseOnlyProgram') ||
              programFeatures.get('enterprise', 'browseOnlyProgram'),
            shouldShowSponsoredByMessage:
              orgFeatures.get('enterprise', 'programSponsoredByMessage') ||
              programFeatures.get('enterprise', 'programSponsoredByMessage'),
            shouldUseJoinTrackingVerbiage:
              orgFeatures.get('enterprise', 'programJoinTrackingVerbiage') ||
              programFeatures.get('enterprise', 'programJoinTrackingVerbiage'),
            allowOrgForCurriculumBuilder:
              orgFeatures.get('enterprise', 'allowOrgForCurriculumBuilder') ||
              programFeatures.get('enterprise', 'allowOrgForCurriculumBuilder'),
            programRecommendationsAvailableCoursesCopy:
              orgFeatures.get('enterprise', 'programRecommendationsAvailableCoursesCopy') ||
              programFeatures.get('enterprise', 'programRecommendationsAvailableCoursesCopy'),
            enableCareerSupport: orgFeatures.get('enterprise', 'enableCareerSupport'),
            isRolePageEstimatedCompletionTimeDisabled:
              orgFeatures.get('enterprise', 'isRolePageEstimatedCompletionTimeDisabled') ||
              programFeatures.get('enterprise', 'isRolePageEstimatedCompletionTimeDisabled'),
            enablePersonalizedRecommendationsForProgram:
              orgFeatures.get('enterprise', 'personalizedRecommendations') ||
              programFeatures.get('enterprise', 'personalizedRecommendations'),
            programEulaBanner:
              orgFeatures.get('enterprise', 'programEulaBanner') ||
              programFeatures.get('enterprise', 'programEulaBanner'),
            allowOrgForSpecializationConfiguration: orgFeatures.get(
              'enterprise',
              'allowOrgForSpecializationConfiguration'
            ),
            enableAcademicDisciplines: orgFeatures.get('enterprise', 'enableAcademicDisciplines'),
            programCollectionSectionDescription:
              orgFeatures.get('enterprise', 'programCollectionSectionDescription') ||
              programFeatures.get('enterprise', 'programCollectionSectionDescription'),
            programHideCourseraRecommendations:
              orgFeatures.get('enterprise', 'programHideCourseraRecommendations') ||
              programFeatures.get('enterprise', 'programHideCourseraRecommendations'),
            programJoinAgeVerification: programFeatures.get('enterprise', 'programJoinAgeVerification'),
            enableEnterpriseCustomBadges:
              orgFeatures.get('enterprise', 'enableEnterpriseCustomBadges') ||
              programFeatures.get('enterprise', 'enableEnterpriseCustomBadges'),
            allowShortFormContentBasedOnAudience:
              orgFeatures.get('enterprise', 'allowShortFormContentBasedOnAudience') ||
              programFeatures.get('enterprise', 'allowShortFormContentBasedOnAudience'),
            enableRenderDomainsMenu: programFeatures.get('enterprise', 'enableRenderDomainsMenu'),
            enableInvitedOnlyProgram:
              orgFeatures.get('enterprise', 'enableInvitedOnlyProgram') ||
              programFeatures.get('enterprise', 'enableInvitedOnlyProgram'),
            showSkillSetRoleFilter: orgFeatures.get('enterprise', 'enableSkillsetRoleFilter'),
            stackabilityOnXdpForEnterpriseLearners: orgFeatures.get(
              'enterprise',
              'stackabilityOnXdpForEnterpriseLearners'
            ),
            enableGenAiHubPages: programFeatures.get('enterprise', 'enableGenAiHubPages'),
            enableOptimizedLearnerFlow: orgFeatures.get('enterprise', 'enableOptimizedLearnerFlow'),
            enableEnterpriseTermsOfUseModal: orgFeatures.get('enterprise', 'enableEnterpriseTermsOfUseModal'),
            filledProgramCustomMessage:
              orgFeatures.get('enterprise', 'filledProgramCustomMessage') ||
              programFeatures.get('enterprise', 'filledProgramCustomMessage'),
            enableForceSsoLogin: orgFeatures.get('enterprise', 'enableForceSsoLogin'),
            enableForceSsoLoginV2Beta: orgFeatures.get('enterprise', 'enableForceSsoLoginV2Beta'),
            enableUnifiedSso: orgFeatures.get('enterprise', 'enableUnifiedSso'),
            enableOptimisedEnrollmentSetting: orgFeatures.get('enterprise', 'enableOptimisedEnrollmentSetting'),
            whitelistOrganizationForSSOLogin: orgFeatures.get('enterprise', 'whitelistOrganizationProgramsForSSOLogin'),
            whitelistOrganizationProgramsForSSOLogin: programFeatures.get(
              'enterprise',
              'whitelistOrganizationProgramsForSSOLogin'
            ),
            enableGenericNotificationMessaging: orgFeatures.get('enterprise', 'enableGenericNotificationMessaging'),
            enableProgramAutoJoin: orgFeatures.get('enterprise', 'enableProgramAutoJoin'),
          },
          isLoading: isOrgProductFeaturesLoading || isProgramProductFeaturesLoading,
        }}
      >
        {children}
      </EnterpriseProductFeaturesContext.Provider>
    );
  }
};

export default EnterpriseProductFeaturesDataProvider;
